import * as React from 'react';

import { useMap } from '../../../provider';

import type { YandexLibrary } from '../../map.types';

import type { MapLoaderProps } from './map.loader.props';

const MapLoader: React.FC<MapLoaderProps> = function(props) {
  const { children } = props;

  const { apikey } = useMap();

  const script = React.useRef<HTMLScriptElement | null>(null);

  const [lib, setLib] = React.useState<YandexLibrary>();

  React.useEffect(() => {
    let isMounted = true;

    script.current = document.querySelector('script[data-yandex-map]');

    const onLoad = () => {
      import('../../lib')
        .then((module) => {
          if (isMounted) {
            module.default().then(setLib);
          }
        })
        .catch((error) => {
          if (isMounted) {
            console.error(error);
          }
        });
    };

    if (!script.current) {
      script.current = document.createElement('script');

      script.current.async             = true;
      script.current.dataset.yandexMap = 'true';
      script.current.src               = `https://api-maps.yandex.ru/v3/?apikey=${apikey}&lang=ru_RU`;

      script.current.onload = () => {
        script.current.dataset.loaded = 'true';
        onLoad();
      };

      document.head.appendChild(script.current);
      // Two <Map/> instances on one page (e.g. Contacts.js's trade-store map + factory map) both
      // reach this effect. The first creates the script tag; the second used to find it already
      // in the DOM and assume that meant "loaded", calling onLoad() (and therefore `ymaps3.import`)
      // immediately - if the script was still fetching/executing, `ymaps3` wasn't a global yet and
      // that map failed with "ymaps3 is not defined" while the first map (which got the real load
      // event) rendered fine. Now every mount either reads the completed flag the loader sets above,
      // or - if the script is still pending - waits for its own `load` event instead of guessing.
    } else if (script.current.dataset.loaded === 'true') {
      onLoad();
    } else {
      script.current.addEventListener('load', onLoad);
    }

    return () => {
      isMounted = false;
    };
  }, [apikey]);

  if (!lib) {
    return null;
  }

  return <React.Suspense fallback={null}>{children(lib)}</React.Suspense>;
};

export default MapLoader;